import {
  AnimatedTerminal,
  type TerminalLine,
} from "@/components/AnimatedTerminal";
import cs from "@/components/cockpit/cockpit.module.css";
import { CockpitLoader } from "@/components/cockpit/CockpitLoader";
import {
  STAGES,
  stageStopSvh,
  STORY_HEIGHT_SVH,
  STORY_QUERY,
  pageTarget,
  type StageId,
} from "@/components/cockpit/stages";
import { Cta } from "@/components/Cta";
import { MediaPlaceholder } from "@/components/MediaPlaceholder";
import { Reveal } from "@/components/Reveal";
import { Section } from "@/components/Section";
import { SectionHeader } from "@/components/SectionHeader";
import { useSeo } from "@/lib/seo";
import { Box, Container, Grid, Text, Title } from "@repo/ui/core";
import {
  IconArrowRight,
  IconBrandAndroid,
  IconBrandAws,
  IconBrandGitlab,
  IconBrandGoogle,
  IconCheck,
  IconCode,
  IconDeviceDesktopBolt,
  IconTestPipe,
} from "@repo/ui/icons";
import styles from "@styles/site.module.css";
import { useReducedMotion } from "framer-motion";
import {
  lazy,
  Suspense,
  useEffect,
  useRef,
  useSyncExternalStore,
  type ComponentType,
  type RefObject,
} from "react";
import { Link } from "react-router";

const CockpitHero = lazy(() => import("@/components/cockpit/CockpitHero"));

const noopSubscribe = () => () => {};

/**
 * The prerender can't wait on the lazy WebGL chunk, so the server and the
 * hydration pass both emit the loader; the scene mounts once hydrated.
 */
function Cockpit({ story = false }: { story?: boolean }) {
  const hydrated = useSyncExternalStore(
    noopSubscribe,
    () => true,
    () => false,
  );
  if (!hydrated) return <CockpitLoader story={story} />;
  return (
    <Suspense fallback={<CockpitLoader story={story} />}>
      <CockpitHero story={story} />
    </Suspense>
  );
}

type Product = {
  title: string;
  description: string;
  icon: ComponentType<{ size?: number | string; stroke?: number | string }>;
  link: string;
  img?: string;
  placeholder?: string;
  span?: string;
};

const products: Product[] = [
  {
    title: "Web Emulators",
    description:
      "Connect to cloud emulators from your browser. Multi-display IVI testing with full ADB and VHAL access.",
    icon: IconDeviceDesktopBolt,
    link: "/products/emulators",
    img: "/null/screenshot.controls.png",
    placeholder:
      "VHAL property editor drawer open on a live emulator session, dark UI",
    span: styles.bentoA,
  },
  {
    title: "Android Builds",
    description:
      "Burst compute for dramatically faster builds. Snapshots restore previous build state in seconds.",
    icon: IconBrandAndroid,
    link: "/products/android-builds",
    span: styles.bentoB,
  },
  {
    title: "Automated Testing",
    description:
      "Behavioral testing for Android, APIs, and full system validation. 40+ languages supported.",
    icon: IconTestPipe,
    link: "/products/testing",
    span: styles.bentoC,
  },
  {
    title: "Remote Development",
    description:
      "Full IDE in the browser via cloud workspaces. Devfiles keep every environment reproducible.",
    icon: IconCode,
    link: "/products/remote-dev",
    img: "/null/screenshot.dev.ext.png",
    placeholder:
      "Browser IDE workspace with an AAOSP repo and emulator panel open",
    span: styles.bentoD,
  },
];

const steps = [
  {
    title: "Push code to GitLab",
    desc: "Standard git workflow. CI/CD triggers automatically on push.",
  },
  {
    title: "Build with burst compute",
    desc: "High-compute runners provision instantly. Snapshots accelerate incremental builds.",
  },
  {
    title: "Deploy to cloud emulator",
    desc: "Your build automatically deploys to an emulator in the cloud.",
  },
  {
    title: "Test and validate",
    desc: "Connect via browser to interact, debug, and run automated behavioral tests.",
  },
];

const pipelineLines: TerminalLine[] = [
  { text: "$ git push origin feat/hvac-controls", kind: "cmd" },
  { text: "✓ Pipeline triggered (#2847)", kind: "ok" },
  { text: "✓ Snapshot restored", kind: "ok" },
  { text: "✓ Dependencies cached", kind: "ok" },
  { text: "→ Building automotive_arm64...", kind: "muted" },
  { text: "  [============================] 100%", kind: "muted" },
  { text: "✓ Build complete (8m 42s)", kind: "ok" },
  { text: "→ Deploying to emulator...", kind: "muted" },
  { text: "✓ Emulator ready", kind: "ok" },
  { text: "  https://review-2847.emu.null.autos", kind: "muted" },
];

const techItems = [
  { label: "GitLab", icon: IconBrandGitlab, color: "orange" },
  { label: "Android Automotive", icon: IconBrandAndroid, color: "lime" },
  { label: "AWS", icon: IconBrandAws, color: "yellow" },
  { label: "Google Cloud", icon: IconBrandGoogle, color: "blue" },
];

const gitlabPoints = [
  "GitLab CI pipeline templates included",
  "GitLab Workspaces for remote development",
  "GitLab package and container registries",
  "Works with Ultimate or Premium licenses",
];

/* --- Cockpit scroll story: chapter copy + terminal panels --- */

const buildsLines: TerminalLine[] = [
  { text: "$ git push origin feat/hvac-controls", kind: "cmd" },
  { text: "✓ Pipeline triggered (#2847)", kind: "ok" },
  { text: "→ fetch, compile, integrate, sign", kind: "muted" },
  { text: "✓ Build complete (8m 42s)", kind: "ok" },
];

const emulatorLines: TerminalLine[] = [
  { text: "$ nullctl emulator create --count 6", kind: "cmd" },
  { text: "✓ emu-01 … emu-06 provisioning", kind: "ok" },
  { text: "✓ 6/6 booted, multi-display ready", kind: "ok" },
];

const testingLines: TerminalLine[] = [
  { text: "$ nullctl test android android", kind: "cmd" },
  { text: "✓ HVAC_TEMPERATURE_SET < 50ms", kind: "ok" },
  { text: "✗ GEAR_SELECTION timeout → retry ✓", kind: "muted" },
  { text: "✓ 412/412 passed", kind: "ok" },
];

function subscribeStory(onChange: () => void): () => void {
  const mq = window.matchMedia(STORY_QUERY);
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
}

// the prerendered HTML (and the hydration pass) uses the story layout: it
// matches most viewports and carries the full stage copy for crawlers
function useStoryMode(): boolean {
  const reduced = useReducedMotion();
  const fits = useSyncExternalStore(
    subscribeStory,
    () => window.matchMedia(STORY_QUERY).matches,
    () => true,
  );
  return fits && !(reduced ?? false);
}

/** Duration (ms) of the ease onto a stop after a swipe is released. */
const PAGE_MS = 450;
/** Scroll quiet time (ms) before a scroll that wasn't a swipe is settled. */
const SETTLE_MS = 140;
/** Release velocity is measured over this trailing window (ms). */
const VELOCITY_WINDOW_MS = 100;

const easeOutCubic = (t: number): number => 1 - (1 - t) ** 3;

/**
 * Touch screens page between stage stops in JS instead of CSS scroll-snap
 * (disabled for coarse pointers in cockpit.module.css): iOS Safari's
 * mandatory snapping often failed to engage while the toolbar resized the
 * viewport, and needed a past-halfway drag to advance.
 *
 * Inside the story a swipe is owned outright: the page follows the finger,
 * and on release pageTarget (stages.ts) picks a stop, eased onto in PAGE_MS.
 * Waiting for native momentum to coast out first made each page take 3-4 s.
 * Pulling down at the very top (pull-to-refresh) and scrolling on past the
 * story's bottom stay native. Scrolls that weren't a swipe here (momentum
 * carried in from below) are settled once they go quiet.
 */
function useTouchPaging(ref: RefObject<HTMLElement | null>, enabled: boolean) {
  useEffect(() => {
    const story = ref.current;
    if (!enabled || !story || !window.matchMedia("(pointer: coarse)").matches)
      return;
    const stops = (): number[] => {
      const ys = [...story.querySelectorAll<HTMLElement>(`:scope > .${cs.snap}`)].map(
        (el) => el.getBoundingClientRect().top + window.scrollY,
      );
      // leaving the story: the next section's top at the viewport top
      return [...ys, story.getBoundingClientRect().bottom + window.scrollY];
    };
    const nearest = (ys: number[], y: number): number =>
      ys.reduce((best, v, i) => (Math.abs(v - y) < Math.abs(ys[best]! - y) ? i : best), 0);

    type Drag = { y0: number; s0: number; samples: { t: number; y: number }[] };
    let drag: Drag | null = null;
    let origin = nearest(stops(), window.scrollY);
    let anim = 0;
    let timer = 0;

    // "instant": global.css sets html { scroll-behavior: smooth } for anchor
    // links, which would otherwise turn every per-frame scroll into its own
    // smooth scroll (the page lags the finger and the ease stretches)
    const jump = (top: number) => window.scrollTo({ top, behavior: "instant" });
    const animateTo = (i: number, ys: number[]) => {
      cancelAnimationFrame(anim);
      origin = i;
      const from = window.scrollY;
      const to = ys[i]!;
      if (Math.abs(to - from) < 1) return;
      const t0 = performance.now();
      const step = (t: number) => {
        const u = Math.min(1, (t - t0) / PAGE_MS);
        jump(from + (to - from) * easeOutCubic(u));
        anim = u < 1 ? requestAnimationFrame(step) : 0;
      };
      anim = requestAnimationFrame(step);
    };
    const settle = (velocity: number) => {
      const ys = stops();
      const next = pageTarget(ys, window.scrollY, origin, velocity);
      if (next !== null) animateTo(next, ys);
    };

    const onTouchStart = (e: TouchEvent) => {
      const ys = stops();
      const y = window.scrollY;
      const touch = e.touches[0];
      drag = null;
      if (!touch || e.touches.length > 1) return;
      if (y < ys[0]! - 1 || y > ys[ys.length - 1]! + 1) return;
      cancelAnimationFrame(anim);
      anim = 0;
      origin = nearest(ys, y);
      drag = { y0: touch.clientY, s0: y, samples: [{ t: e.timeStamp, y }] };
    };
    const onTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!drag || !touch) return;
      const ys = stops();
      const first = ys[0]!;
      const end = ys[ys.length - 1]!;
      const y = drag.s0 + (drag.y0 - touch.clientY);
      // hand the gesture to the browser: pull-to-refresh from the top, or
      // scrolling on into the page below the story
      if ((y < first && drag.s0 <= first + 1) || (y > end && drag.s0 >= end - 1)) {
        drag = null;
        return;
      }
      e.preventDefault();
      const clamped = Math.min(Math.max(y, first), end);
      jump(clamped);
      drag.samples.push({ t: e.timeStamp, y: clamped });
      if (drag.samples.length > 8) drag.samples.shift();
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (!drag) return;
      const recent = drag.samples.filter((p) => e.timeStamp - p.t <= VELOCITY_WINDOW_MS);
      const a = recent[0];
      const b = recent[recent.length - 1];
      const velocity = a && b && b.t > a.t ? (b.y - a.y) / (b.t - a.t) : 0;
      drag = null;
      settle(velocity);
    };
    const onScroll = () => {
      window.clearTimeout(timer);
      if (drag || anim) return;
      timer = window.setTimeout(() => {
        if (!drag && !anim) settle(0);
      }, SETTLE_MS);
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("touchcancel", onTouchEnd, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(anim);
      window.clearTimeout(timer);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
      window.removeEventListener("scroll", onScroll);
    };
  }, [ref, enabled]);
}

function HeroCopy() {
  return (
    <div>
      <Reveal>
        <Title order={1} className={styles.heroTitle}>
          Ship automotive software{" "}
          <span className={styles.gradientText}>faster</span>.
        </Title>
      </Reveal>
      <Reveal delay={0.07}>
        <p className={styles.heroSubtitle} style={{ marginTop: 20 }}>
          Build, test, and deploy Android Automotive on cloud-native
          infrastructure that plugs into your GitLab workflow.
        </p>
      </Reveal>
      <Reveal delay={0.14}>
        <div className={styles.ctaRow}>
          <Cta to="/contact">Book a Demo</Cta>
          <Cta
            to="/platform"
            variant="ghost"
            iconRight={<IconArrowRight size={16} />}
          >
            Explore Platform
          </Cta>
        </div>
      </Reveal>
    </div>
  );
}

type StoryStep = {
  stage: StageId;
  heading?: string;
  sub?: string;
  terminal?: { title: string; lines: TerminalLine[] };
};

const storySteps: StoryStep[] = [
  {
    stage: "stack",
    heading: "What's in a build?",
    sub: "Nine layers, dozens of vendors, each with its own toolchain and release cadence, compiled, integrated, and signed into one bootable image.",
  },
  {
    stage: "builds",
    heading: "Push code. Get a bootable image.",
    sub: "Burst compute and build snapshots turn a full AAOSP build from an overnight job into minutes.",
    terminal: { title: "android-builds.log", lines: buildsLines },
  },
  {
    stage: "emulate",
    heading: "One build, a fleet of cockpits.",
    sub: "Every merge request gets its own emulators in the browser. No bench hardware.",
    terminal: { title: "nullctl", lines: emulatorLines },
  },
  {
    stage: "test",
    heading: "Every signal, every property, every run.",
    sub: "Behavioral tests drive VHAL properties on real emulators and catch regressions before the road does.",
    terminal: { title: "test-runner.log", lines: testingLines },
  },
  {
    stage: "deploy",
    heading: "Code, CI, and the cockpit in one tab.",
    sub: "Cloud workspaces put your editor, your pipeline, and a live emulator side by side in the browser. Push a change and watch the new build boot on the dash.",
  },
];

const stageIndex = (id: StageId): number =>
  STAGES.findIndex((st) => st.id === id);

/** Copy for a stage sits vertically centered on the viewport when snapped to its stop. */
const stepTop = (id: StageId): string =>
  `${stageStopSvh(stageIndex(id)) + 50}svh`;

export default function Home() {
  useSeo("/");
  const storyMode = useStoryMode();
  const storyRef = useRef<HTMLElement>(null);
  useTouchPaging(storyRef, storyMode);

  return (
    <>
      {/* Hero: pinned cockpit scroll story (desktop) / assembled cockpit (mobile, reduced motion) */}
      {storyMode ? (
        <section
          ref={storyRef}
          className={cs.story}
          id="home-hero"
          data-cockpit-story
          style={{ height: `${STORY_HEIGHT_SVH}svh` }}
        >
          {STAGES.map((st, i) => (
            <div
              key={st.id}
              className={cs.snap}
              style={{ top: `${stageStopSvh(i)}svh` }}
            />
          ))}
          <div className={cs.storyStage}>
            <Cockpit story />
          </div>
          <div className={cs.storySteps}>
            <div className={`${cs.step} ${cs.stepHero}`} style={{ top: "16svh" }}>
              <div className={cs.stepBody}>
                <HeroCopy />
              </div>
            </div>
            {storySteps.map((step) => (
              <div
                key={step.stage}
                className={`${cs.step} ${cs.stepCentered}`}
                style={{ top: stepTop(step.stage) }}
              >
                <div className={cs.stepBody}>
                  {step.heading && (
                    <h2 className={cs.stepHeading}>{step.heading}</h2>
                  )}
                  {step.sub && <p className={cs.stepSub}>{step.sub}</p>}
                  {step.terminal && (
                    <div className={cs.stepTerminal}>
                      <AnimatedTerminal
                        title={step.terminal.title}
                        lines={step.terminal.lines}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
            <div
              className={`${cs.step} ${cs.stepCentered}`}
              style={{ top: stepTop("ignition") }}
            >
              <div className={cs.stepBody}>
                <h2 className={cs.stepHeading}>
                  From commit to{" "}
                  <span className={styles.gradientText}>cockpit</span>.
                </h2>
                <p className={cs.stepSub}>
                  Build, test, and deploy Android Automotive on cloud-native
                  infrastructure.
                </p>
                <div className={styles.ctaRow}>
                  <Cta to="/contact">Book a Demo</Cta>
                  <Cta
                    to="/platform"
                    variant="ghost"
                    iconRight={<IconArrowRight size={16} />}
                  >
                    Explore Platform
                  </Cta>
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <div className={styles.hero} id="home-hero">
          <Container size="xl" className={styles.heroInner}>
            <div className={styles.heroGrid}>
              <HeroCopy />
              <Reveal delay={0.2} y={28}>
                <div className={styles.heroStage}>
                  <Cockpit />
                </div>
              </Reveal>
            </div>
          </Container>
        </div>
      )}

      <div className={storyMode ? cs.afterStory : undefined}>
        {/* Tech strip */}
        <Section tight hairline>
          <Reveal>
            <div className={styles.techStrip}>
              <Text size="sm" c="dimmed">
                Works with the tools your teams already use
              </Text>
              {techItems.map((item) => (
                <span key={item.label} className={styles.techItem}>
                  <item.icon size={18} stroke={1.5} color={item.color} />
                  {item.label}
                </span>
              ))}
            </div>
          </Reveal>
        </Section>

        {/* Products bento */}
        <Section hairline>
          <SectionHeader
            eyebrow="Products"
            title="One platform. Every workflow."
            description="From code commit to running emulators, everything works with your existing CI/CD workflow."
          />
          <div className={styles.bentoGrid}>
            {products.map((product, i) => (
              <Reveal
                key={product.title}
                delay={i * 0.06}
                className={product.span}
              >
                <Link
                  to={product.link}
                  className={`${styles.card} ${styles.cardLink} ${styles.bentoCell}`}
                  style={{ height: "100%" }}
                >
                  {product.img ? (
                    <div className={styles.bentoCellMedia}>
                      <img src={product.img} alt="" loading="lazy" />
                    </div>
                  ) : product.placeholder ? (
                    <div className={styles.bentoCellMedia}>
                      <MediaPlaceholder fill label={product.placeholder} />
                    </div>
                  ) : null}
                  <div className={styles.bentoCellBody}>
                    {!product.img && !product.placeholder && (
                      <span
                        className={styles.iconChip}
                        style={{ marginBottom: 8 }}
                      >
                        <product.icon size={20} stroke={1.5} />
                      </span>
                    )}
                    <Title order={3} size="1.25rem" fw={600}>
                      {product.title}
                    </Title>
                    <Text size="sm" c="dimmed" lh={1.65}>
                      {product.description}
                    </Text>
                    <span className={styles.bentoLink}>
                      Learn more <IconArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </Section>

        {/* Pipeline */}
        <Section hairline>
          <Grid gutter={64} align="center">
            <Grid.Col span={{ base: 12, md: 6 }}>
              <SectionHeader
                title="From commit to emulator in one pipeline"
                description="See your code running on a cloud emulator in minutes. No hardware, no manual setup."
              />
              <div>
                {steps.map((step, i) => (
                  <Reveal key={step.title} delay={i * 0.05}>
                    <div className={styles.stepRow}>
                      <div>
                        <Text fw={600} mb={4}>
                          {step.title}
                        </Text>
                        <Text size="sm" c="dimmed" lh={1.6}>
                          {step.desc}
                        </Text>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Reveal delay={0.1} y={24}>
                <AnimatedTerminal
                  title="build-pipeline.log"
                  lines={pipelineLines}
                />
              </Reveal>
            </Grid.Col>
          </Grid>
        </Section>

        {/* GitLab */}
        <Section hairline>
          <SectionHeader
            align="center"
            title="Works with GitLab"
            description="Null Autos works with GitLab Dedicated and SaaS. Use your existing CI pipelines, workspaces, and package registries. We handle the automotive-specific infrastructure."
          />
          <Reveal>
            <Box maw={720} mx="auto" mb={48}>
              <ul
                className={styles.checkList}
                style={{
                  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                }}
              >
                {gitlabPoints.map((point) => (
                  <li key={point}>
                    <IconCheck size={16} stroke={2.5} />
                    {point}
                  </li>
                ))}
              </ul>
            </Box>
          </Reveal>
          <Reveal delay={0.08} y={24}>
            <div className={styles.mediaFrame}>
              <MediaPlaceholder
                label="GitLab CI pipeline running the Null Autos AAOSP build template, all stages green"
                ratio="21 / 9"
                src="/null/screenshot.pipeline.png"
              />
              <div className={styles.frameCaption}>
                GitLab CI / pipeline templates
              </div>
            </div>
          </Reveal>
        </Section>

        {/* Final CTA */}
        <section className={styles.finalCta}>
          <Container size="xl" py={96} ta="center">
            <Reveal>
              <Title
                order={2}
                fw={650}
                style={{
                  fontSize: "clamp(1.875rem, 3.5vw, 2.75rem)",
                  letterSpacing: "-0.025em",
                  lineHeight: 1.12,
                  textWrap: "balance",
                }}
              >
                Ready to accelerate your{" "}
                <span className={styles.gradientText}>
                  automotive development
                </span>
                ?
              </Title>
            </Reveal>
            <Reveal delay={0.07}>
              <Text size="lg" c="dimmed" maw={520} mx="auto" mt="md" lh={1.65}>
                See how Null Autos fits your Android Automotive workflow.
                Schedule a demo with our team.
              </Text>
            </Reveal>
            <Reveal delay={0.14}>
              <div
                className={styles.ctaRow}
                style={{ justifyContent: "center" }}
              >
                <Cta to="/contact">Book a Demo</Cta>
                <Cta
                  to="/platform"
                  variant="ghost"
                  iconRight={<IconArrowRight size={16} />}
                >
                  Explore Platform
                </Cta>
              </div>
            </Reveal>
          </Container>
        </section>
      </div>
    </>
  );
}
